import { useMemo } from 'react';
import { graphql } from 'react-relay';
import type { GraphQLTaggedNode } from 'react-relay';

import { useEditorContentFragment } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';
import type { EditorContentFragmentResult } from '@confluence/editor-content-unified-query/entry-points/useEditorContentFragment.ts';

import type { PageOwnershipByLineWithCollaboratorsDropdownContentQuery$data } from './__generated__/PageOwnershipByLineWithCollaboratorsDropdownContentQuery.graphql.ts';
import type { useEditorCollaboratorsFragment_content$key } from './__generated__/useEditorCollaboratorsFragment_content.graphql.ts';
import type { useEditorCollaboratorsFragment_query$key } from './__generated__/useEditorCollaboratorsFragment_query.graphql.ts';

// The page status and space admin of the collaborators query, read from the editor document.
const editorCollaboratorsFragmentNode: GraphQLTaggedNode = graphql`
	fragment useEditorCollaboratorsFragment_content on Content @catch {
		id
		status
		space {
			id
			currentUser {
				isAdmin
			}
		}
	}
`;

// The arguments match the collaborators query, whose refreshes after a change update this field.
const editorCollaboratorsListFragmentNode: GraphQLTaggedNode = graphql`
	fragment useEditorCollaboratorsFragment_query on Query
	@argumentDefinitions(cloudId: { type: "ID!" }, contentId: { type: "ID!" })
	@catch {
		confluence_contentCollaborators(cloudId: $cloudId, id: $contentId, first: 2147483647) {
			count
			nodes {
				person {
					type
					displayName
					profilePicture {
						path
					}
					... on KnownUser {
						accountId
						jobTitle
					}
				}
				agent {
					displayName
					accountId
					profilePicture {
						path
					}
				}
			}
		}
	}
`;

/**
 * The page collaborators from EditorContentUnifiedQuery in the shape of the collaborators query
 * data. When it is provided, the collaborators byline skips that query.
 */
export function useEditorCollaboratorsFragment({
	contentId,
}: {
	contentId: string | undefined;
}): EditorContentFragmentResult<PageOwnershipByLineWithCollaboratorsDropdownContentQuery$data> {
	const content = useEditorContentFragment<useEditorCollaboratorsFragment_content$key>(
		'editorCollaboratorsFragment',
		editorCollaboratorsFragmentNode,
		{ contentId },
	);
	const collaborators = useEditorContentFragment<useEditorCollaboratorsFragment_query$key>(
		'editorCollaboratorsListFragment',
		editorCollaboratorsListFragmentNode,
		{ contentId },
	);

	return useMemo(
		() => ({
			isProvided: content.isProvided && collaborators.isProvided,
			loading: content.loading || collaborators.loading,
			data:
				content.data && collaborators.data
					? {
							content: { nodes: [{ id: content.data.id, status: content.data.status }] },
							space: content.data.space,
							confluence_contentCollaborators: collaborators.data.confluence_contentCollaborators,
						}
					: undefined,
			error: content.error ?? collaborators.error,
		}),
		[content, collaborators],
	);
}
